import { useState } from "react";
import { ImageOff, ZoomIn } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ProductSummaryDto } from "@hisense/shared";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";

type PhotoProduct = Pick<
  ProductSummaryDto,
  "productCode" | "subRef" | "description" | "color" | "size" | "imageUrl"
>;

function Photo({
  url,
  label,
  large = false,
}: {
  url: string;
  label: string;
  large?: boolean;
}) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  if (!url || failed)
    return (
      <div
        className={`flex items-center justify-center gap-2 text-ink-faint ${large ? "min-h-56 flex-col p-6" : "h-full w-full"}`}
      >
        <ImageOff className={large ? "size-8" : "size-5"} aria-hidden="true" />
        <span className={large ? "text-sm" : "sr-only"}>
          {t(
            failed
              ? "quotationWorkspace.photoFailed"
              : "quotationWorkspace.photoMissing",
          )}
        </span>
        {large && failed && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setFailed(false);
              setLoaded(false);
              setAttempt((n) => n + 1);
            }}
          >
            {t("common.retry")}
          </Button>
        )}
      </div>
    );
  return (
    <>
      {large && !loaded && (
        <p role="status" className="p-3 text-center text-xs text-ink-faint">
          {t("common.loading")}
        </p>
      )}
      <img
        key={attempt}
        src={url}
        alt={label}
        loading={large ? "eager" : "lazy"}
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        className={
          large
            ? "max-h-[55dvh] w-full object-contain"
            : "h-full w-full object-contain"
        }
      />
    </>
  );
}

/** Viewing an image never selects a product. Selection is a separate explicit action. */
export function QuotationProductImage({
  product,
  onSelect,
  selected = false,
  compact = false,
}: {
  product: PhotoProduct;
  onSelect?: () => void;
  selected?: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = [product.productCode, product.subRef].filter(Boolean).join(" / ");
  const label = t("quotationWorkspace.photoOf", { ref });
  return (
    <>
      <button
        type="button"
        disabled={!ref && !product.imageUrl}
        onClick={() => setOpen(true)}
        aria-label={ref ? t("quotationWorkspace.previewPhoto", { ref }) : t("quotationWorkspace.photoPreview")}
        title={t("quotationWorkspace.previewPhoto", { ref })}
        className={`relative grid shrink-0 place-items-center overflow-hidden rounded-sm border border-line bg-surface focus-visible:outline-2 focus-visible:outline-brand ${compact ? "size-9" : "size-14"}`}
      >
        <Photo
          key={product.imageUrl ?? ""}
          url={product.imageUrl ?? ""}
          label={label}
        />
        {product.imageUrl && (
          <span className="absolute bottom-0 right-0 rounded-tl-sm bg-surface/90 p-0.5 text-brand">
            <ZoomIn className="size-3" aria-hidden="true" />
          </span>
        )}
      </button>
      {open && (
        <Modal
          eyebrow={t("quotationWorkspace.photoPreview")}
          title={ref || t("common.image")}
          closeLabel={t("common.close")}
          onClose={() => setOpen(false)}
          className="max-w-3xl"
        >
          <div className="min-h-0 overflow-y-auto p-4">
            <div className="overflow-hidden rounded-md border border-line bg-surface p-2">
              <Photo
                key={product.imageUrl ?? ""}
                url={product.imageUrl ?? ""}
                label={label}
                large
              />
            </div>
            <p className="mt-3 text-sm text-ink">
              {product.description || "—"}
            </p>
            <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-2 text-xs text-ink-soft">
              <div>
                <dt className="inline">{t("common.color")}: </dt>
                <dd className="inline">{product.color || "—"}</dd>
              </div>
              <div>
                <dt className="inline">{t("common.size")}: </dt>
                <dd className="inline">{product.size || "—"}</dd>
              </div>
            </dl>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-line bg-surface p-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setOpen(false)}
            >
              {t("common.close")}
            </Button>
            {onSelect && (
              <Button
                size="sm"
                disabled={selected}
                onClick={() => {
                  if (selected) return;
                  onSelect();
                  setOpen(false);
                }}
              >
                {t(
                  selected
                    ? "quotationWorkspace.productSelected"
                    : "quotationWorkspace.selectProduct",
                )}
              </Button>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
